import {
	formatMoney,
	type ProductCard,
	type RecipeInput,
	recipeDuration,
	recipeLine,
} from "@commerce/shopper/types";
import { Link, useParams } from "@tanstack/react-router";
import { Badge } from "@ui/components/badge";
import { buttonVariants } from "@ui/components/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@ui/components/collapsible";
import { Spinner } from "@ui/components/spinner";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@ui/components/tooltip";
import { cn } from "@ui/lib";
import {
	BookmarkIcon,
	BookOpenIcon,
	ChevronRightIcon,
	CodeIcon,
	HeadsetIcon,
	MailXIcon,
	PackageIcon,
	PhoneOffIcon,
	RepeatIcon,
	ScanSearchIcon,
	SearchIcon,
	ShoppingBasketIcon,
	ShoppingCartIcon,
	StoreIcon,
	TriangleAlertIcon,
	TruckIcon,
	WrenchIcon,
	XCircleIcon,
} from "lucide-react";
import { useState } from "react";

/**
 * One agent tool call, collapsed to a line a retailer can read ("Searched for
 * “almarai milk” · 8 products") and expandable to the ranked results, the
 * basket after the change, and the raw input and output for engineers.
 */

interface ToolPartLike {
	toolName: string;
	state: string;
	input: unknown;
	output: unknown;
	errorText: string | undefined;
}

interface BasketOutput {
	currency: string;
	lines: Array<{
		productId: string;
		title: string;
		quantity: number;
		lineTotal: string;
		inStock: boolean;
	}>;
	itemCount: number;
	total: string;
}

type Obj = Record<string, unknown>;
const asObj = (value: unknown): Obj =>
	value && typeof value === "object" ? (value as Obj) : {};
const asArray = (value: unknown): unknown[] =>
	Array.isArray(value) ? value : [];

const TOOL_META: Record<string, { icon: typeof SearchIcon; verb: string }> = {
	search_products: { icon: SearchIcon, verb: "Searched for" },
	find_ingredients: { icon: BookOpenIcon, verb: "Found ingredients for" },
	get_product: { icon: PackageIcon, verb: "Looked at" },
	add_to_basket: { icon: ShoppingBasketIcon, verb: "Added to the basket" },
	update_basket: { icon: ShoppingBasketIcon, verb: "Changed the basket" },
	clear_basket: { icon: ShoppingBasketIcon, verb: "Emptied the basket" },
	get_basket: { icon: ShoppingBasketIcon, verb: "Checked the basket" },
	set_substitution: {
		icon: ShoppingBasketIcon,
		verb: "Set replacements to",
	},
	get_order_status: { icon: TruckIcon, verb: "Checked the order" },
	escalate_to_human: { icon: HeadsetIcon, verb: "Handed over to your team" },
	record_memory_consent: {
		icon: BookmarkIcon,
		verb: "Noted their memory choice",
	},
	remember: { icon: BookmarkIcon, verb: "Remembered" },
	forget_memory: { icon: BookmarkIcon, verb: "Forgot what was saved" },
	set_store: { icon: StoreIcon, verb: "Moved the chat to" },
	set_marketing_consent: {
		icon: MailXIcon,
		verb: "Noted their offers choice",
	},
	start_checkout: { icon: ShoppingCartIcon, verb: "Opened the checkout" },
	reorder: { icon: RepeatIcon, verb: "Started a reorder of" },
	cancel_order: { icon: XCircleIcon, verb: "Asked to cancel order" },
	report_order_issue: {
		icon: TriangleAlertIcon,
		verb: "Reported a problem with order",
	},
	end_call: { icon: PhoneOffIcon, verb: "Ended the call" },
};

type Tone = "default" | "alarm" | "handover";

const TONE_SURFACE: Record<Tone, string> = {
	handover:
		"bg-warning/10 ring-warning/35 dark:bg-warning/8 dark:ring-warning/25",
	alarm: "bg-destructive/5 ring-destructive/20 dark:bg-destructive/10",
	default: "bg-muted/45 ring-foreground/5 dark:ring-foreground/8",
};

const TONE_ICON: Record<Tone, string | null> = {
	handover: "text-warning-foreground dark:text-warning",
	alarm: "text-destructive",
	default: null,
};

const TONE_BADGE = {
	handover: "warning",
	alarm: "destructive",
	default: "secondary",
} as const satisfies Record<Tone, string>;

/** Where a call stands, from its part state and whether the reply is still streaming. */
function callState(part: ToolPartLike, streaming: boolean) {
	const handover = part.toolName === "escalate_to_human";
	const failed = part.state === "output-error";
	const pending =
		part.state === "input-streaming" || part.state === "input-available";
	const running = pending && streaming;
	const summary = summarize(part);
	let tone: Tone = "default";
	if (handover) {
		tone = "handover";
	} else if (failed || summary.alarming) {
		tone = "alarm";
	}
	let result = summary.result;
	if (failed) {
		result = "Failed";
	} else if (running) {
		result = "Working";
	} else if (pending) {
		result = "Didn't finish";
	}
	return { failed, running, summary, tone, result };
}

export function ToolCall({
	part,
	streaming = false,
}: {
	part: ToolPartLike;
	/** The reply is still streaming, so an unfinished call is running, not abandoned. */
	streaming?: boolean;
}) {
	// A handover is the one call a retailer always needs to read in full.
	const [open, setOpen] = useState(part.toolName === "escalate_to_human");
	const { failed, running, summary, tone, result } = callState(
		part,
		streaming,
	);

	return (
		<Collapsible
			open={open}
			onOpenChange={setOpen}
			className={cn(
				"w-full min-w-0 overflow-hidden rounded-2xl ring-1",
				TONE_SURFACE[tone],
			)}
		>
			<div className="flex items-center">
				<CollapsibleTrigger className="flex min-w-0 flex-1 items-center gap-2.5 px-2.5 py-2 text-left text-sm outline-none transition-colors hover:bg-foreground/[0.03] focus-visible:ring-3 focus-visible:ring-ring/30">
					<ToolLine
						toolName={part.toolName}
						summary={summary}
						tone={tone}
						running={running}
					/>
					{result ? (
						<Badge
							variant={TONE_BADGE[tone]}
							className="tabular max-sm:hidden"
						>
							{result}
						</Badge>
					) : null}
					<ChevronRightIcon
						className={cn(
							"size-4 shrink-0 text-muted-foreground transition-transform duration-(--duration-hover) ease-(--ease-out-ui)",
							open && "rotate-90",
						)}
						aria-hidden
					/>
				</CollapsibleTrigger>
				{part.toolName === "search_products" ? (
					<CheckSearchLink
						query={String(asObj(part.input)["query"] ?? "")}
					/>
				) : null}
			</div>
			<CollapsibleContent className="border-foreground/5 border-t bg-background/60 dark:bg-background/30">
				<div className="flex flex-col gap-3 px-3 py-3">
					{result ? (
						<p className="text-muted-foreground text-xs sm:hidden">
							{result}
						</p>
					) : null}
					{failed ? (
						<p className="text-destructive text-sm">
							{part.errorText ?? "The tool returned an error."}
						</p>
					) : (
						<ToolBody part={part} />
					)}
					<RawData input={part.input} output={part.output} />
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
}

/** The collapsed call in words: an icon, the verb, what it acted on, and its context. */
function ToolLine({
	toolName,
	summary,
	tone,
	running,
}: {
	toolName: string;
	summary: Summary;
	tone: Tone;
	running: boolean;
}) {
	const meta = TOOL_META[toolName] ?? {
		icon: WrenchIcon,
		verb: toolName.replaceAll("_", " "),
	};
	return (
		<>
			<span
				className={cn(
					"grid size-6 shrink-0 place-items-center rounded-lg bg-background text-muted-foreground ring-1 ring-foreground/5",
					TONE_ICON[tone],
				)}
			>
				{running ? (
					<Spinner className="size-3.5" />
				) : (
					<meta.icon className="size-3.5" aria-hidden />
				)}
			</span>
			<span className="min-w-0 flex-1 truncate">
				<span
					className={cn(
						tone === "handover"
							? "font-medium text-foreground"
							: "text-muted-foreground",
					)}
				>
					{meta.verb}
				</span>
				{summary.subject ? (
					<span className="font-medium text-foreground">
						{" "}
						{summary.quoted ? "“" : null}
						<bdi>{summary.subject}</bdi>
						{summary.quoted ? "”" : null}
					</span>
				) : null}
				{summary.suffix ? (
					<span className="text-muted-foreground">
						{" "}
						{summary.suffix}
					</span>
				) : null}
			</span>
		</>
	);
}

interface Summary {
	subject: string | null;
	/** Shopper words: rendered in quotes and isolated so Arabic keeps its direction. */
	quoted?: boolean;
	/** Plain context after the subject ("in Groceries, under 5"). */
	suffix?: string;
	result: string;
	alarming: boolean;
}

function summarize(part: ToolPartLike): Summary {
	const input = asObj(part.input);
	const output = asObj(part.output);
	switch (part.toolName) {
		case "search_products": {
			const products = (output["products"] as ProductCard[]) ?? [];
			const queries = [
				input["query"],
				...(Array.isArray(input["alsoSearch"])
					? input["alsoSearch"]
					: []),
			].filter((q): q is string => typeof q === "string" && q !== "");
			const filters = [
				// No query lists today's deals, which are on sale already.
				input["onSale"] && queries.length ? "on sale" : null,
				input["department"] ? `in ${input["department"]}` : null,
				input["maxPrice"] ? `under ${input["maxPrice"]}` : null,
				input["totalBudget"]
					? `${input["totalBudget"]} for the list`
					: null,
				Array.isArray(input["exclude"]) && input["exclude"].length
					? "more options"
					: null,
			].filter(Boolean);
			return {
				// An empty query lists the day's deals.
				subject: queries.length ? queries.join(", ") : "today's deals",
				quoted: queries.length > 0,
				suffix: filters.join(", "),
				result:
					part.output === undefined
						? ""
						: products.length === 0
							? "No products"
							: `${products.length} product${products.length === 1 ? "" : "s"}`,
				alarming: part.output !== undefined && products.length === 0,
			};
		}
		case "find_ingredients": {
			const matches =
				(output["matches"] as Array<{ best: unknown }>) ?? [];
			const missing = matches.filter((m) => !m.best).length;
			return {
				subject: input["dish"]
					? String(input["dish"])
					: `${(input["ingredients"] as unknown[] | undefined)?.length ?? 0} ingredients`,
				result: missing
					? `${missing} of ${matches.length} missing`
					: `${matches.length} found`,
				alarming: missing > 0,
			};
		}
		case "add_to_basket":
		case "update_basket":
		case "clear_basket":
		case "get_basket": {
			const basket = output["basket"] as BasketOutput | undefined;
			const errors = (output["errors"] as unknown[]) ?? [];
			return {
				subject: null,
				result: errors.length
					? `${errors.length} problem${errors.length === 1 ? "" : "s"}`
					: basket
						? `${basket.itemCount} item${basket.itemCount === 1 ? "" : "s"} · ${formatMoney(basket.total, basket.currency)}`
						: "",
				alarming: errors.length > 0,
			};
		}
		case "get_product":
			return {
				subject: output["title"]
					? String(output["title"])
					: "a product",
				result: output["error"] ? "Not found" : "",
				alarming: Boolean(output["error"]),
			};
		case "set_substitution": {
			const errors = (output["errors"] as unknown[]) ?? [];
			return {
				subject: input["preference"]
					? String(input["preference"]).toLowerCase()
					: null,
				result: errors.length
					? `${errors.length} problem${errors.length === 1 ? "" : "s"}`
					: "",
				alarming: errors.length > 0,
			};
		}
		case "get_order_status": {
			const orders = (output["orders"] as unknown[]) ?? [];
			return {
				subject: input["orderId"] ? String(input["orderId"]) : null,
				result:
					part.output === undefined
						? ""
						: orders.length === 0
							? "No order found"
							: `${orders.length} order${orders.length === 1 ? "" : "s"}`,
				alarming: part.output !== undefined && orders.length === 0,
			};
		}
		case "escalate_to_human":
			return { subject: null, result: "Needs a person", alarming: true };
		case "set_store": {
			const store = asObj(output["store"]);
			const removed = asArray(asObj(output["movedBasket"])["removed"]);
			return {
				subject: store["name"]
					? String(store["name"])
					: input["area"]
						? String(input["area"])
						: null,
				quoted: !store["name"] && Boolean(input["area"]),
				result:
					part.output === undefined
						? ""
						: !output["store"]
							? output["reason"] === "ambiguous"
								? "More than one branch"
								: "No branch delivers there"
							: removed.length
								? `${removed.length} not sold there`
								: "",
				alarming: part.output !== undefined && !output["store"],
			};
		}
		case "set_marketing_consent":
			return {
				subject: null,
				result:
					input["value"] === "stop"
						? "No more offers"
						: "Offers again",
				alarming: false,
			};
		case "start_checkout":
		case "reorder":
		case "cancel_order":
		case "report_order_issue": {
			const error = output["error"] ?? output["reason"];
			return {
				subject: input["orderId"] ? String(input["orderId"]) : null,
				result: error ? String(error).replaceAll("_", " ") : "",
				alarming: Boolean(error),
			};
		}
		case "end_call":
			return {
				subject: null,
				result:
					input["reason"] === "caller_asked"
						? "The caller asked"
						: "The caller was done",
				alarming: false,
			};
		default:
			return { subject: null, result: "", alarming: false };
	}
}

function ToolBody({ part }: { part: ToolPartLike }) {
	const input = asObj(part.input);
	const output = asObj(part.output);

	switch (part.toolName) {
		case "search_products":
			return (
				<SearchResults
					department={input["department"]}
					output={output}
					finished={part.output !== undefined}
				/>
			);
		case "find_ingredients":
			return (
				<IngredientMatches
					recipe={input as RecipeInput}
					matches={(output["matches"] as IngredientMatch[]) ?? []}
				/>
			);
		case "add_to_basket":
		case "update_basket":
		case "clear_basket":
		case "get_basket":
			return (
				<BasketResult
					basket={output["basket"] as BasketOutput | undefined}
					errors={(output["errors"] as BasketError[]) ?? []}
				/>
			);
		case "get_product":
			return output["error"] ? (
				<p className="text-destructive text-sm">
					No product with id {String(input["productId"])}.
				</p>
			) : (
				<ProductFacts product={output} />
			);
		case "escalate_to_human":
			return (
				<div className="flex flex-col gap-1 text-sm">
					<p className="text-muted-foreground text-xs">
						Why the agent handed over
					</p>
					<p dir="auto" className="whitespace-pre-wrap">
						{String(input["reason"] ?? "No reason given.")}
					</p>
				</div>
			);
		default:
			return null;
	}
}

function SearchResults({
	department,
	output,
	finished,
}: {
	department: unknown;
	output: Obj;
	/** The search returned; before that there is nothing to say about an empty page. */
	finished: boolean;
}) {
	const products = (output["products"] as ProductCard[]) ?? [];
	return (
		<>
			{output["departmentRelaxed"] ? (
				<p className="text-muted-foreground text-xs">
					Includes better matches from outside {String(department)}.
				</p>
			) : null}
			{products.length ? (
				<ol className="flex flex-col divide-y divide-border/70">
					{products.map((p, i) => (
						<li key={p.id}>
							<ProductRow product={p} rank={i + 1} />
						</li>
					))}
				</ol>
			) : finished ? (
				<p className="text-sm">
					Nothing matched. Sold here? Check this search to see why.
				</p>
			) : null}
		</>
	);
}

interface IngredientMatch {
	ingredient: string;
	best: ProductCard | null;
	quantity?: number;
	alternatives: ProductCard[];
}

/**
 * Keys from content for lists without ids: a value that repeats gets its occurrence
 * number, so two equal lines stay distinct.
 */
function withContentKeys<T>(
	items: readonly T[],
	text: (item: T) => string,
): Array<{ key: string; item: T }> {
	const seen = new Map<string, number>();
	return items.map((item) => {
		const value = text(item);
		const count = (seen.get(value) ?? 0) + 1;
		seen.set(value, count);
		return { key: count === 1 ? value : `${value}#${count}`, item };
	});
}

function IngredientMatches({
	recipe,
	matches,
}: {
	recipe: RecipeInput;
	matches: readonly IngredientMatch[];
}) {
	// The tool answers the recipe's ingredient lines in order.
	const rows = withContentKeys(
		matches.map((match, i) => ({
			match,
			line: recipeLine(recipe.ingredients?.[i]),
		})),
		(row) => row.match.ingredient,
	);
	const steps = withContentKeys(
		recipe.steps?.filter((step): step is string => Boolean(step)) ?? [],
		(step) => step,
	);
	return (
		<>
			{recipe.servings || recipe.minutes ? (
				<p className="text-muted-foreground text-xs">
					{[
						recipe.servings && `Serves ${recipe.servings}`,
						recipe.minutes && recipeDuration(recipe.minutes),
					]
						.filter(Boolean)
						.join(" · ")}
				</p>
			) : null}
			<ul className="flex flex-col gap-2.5">
				{rows.map(({ key, item: { match, line } }) => (
					<li key={key}>
						<p
							className="font-medium text-muted-foreground text-xs"
							dir="auto"
						>
							{ingredientLabel(match, line)}
						</p>
						{match.best ? (
							<ProductRow product={match.best} />
						) : (
							<p className="py-1 text-destructive text-sm">
								Nothing in stock matches
							</p>
						)}
					</li>
				))}
			</ul>
			{steps.length ? (
				<ol
					dir="auto"
					className="flex list-decimal flex-col gap-1 ps-5 text-sm marker:text-muted-foreground"
				>
					{steps.map(({ key, item }) => (
						<li key={key}>{item}</li>
					))}
				</ol>
			) : null}
		</>
	);
}

/** "2 onions (onion) · 2 packs · 3 alternatives": the line as written, then what was found. */
function ingredientLabel(
	match: IngredientMatch,
	line: ReturnType<typeof recipeLine>,
): string {
	const parts = [
		line && line.text !== match.ingredient
			? `${line.text} (${match.ingredient})`
			: match.ingredient,
	];
	if (line?.pantry) {
		parts.push("pantry");
	}
	if (match.quantity && match.quantity > 1) {
		parts.push(`${match.quantity} packs`);
	}
	if (match.alternatives.length) {
		parts.push(
			`${match.alternatives.length} alternative${match.alternatives.length === 1 ? "" : "s"}`,
		);
	}
	return parts.join(" · ");
}

interface BasketError {
	error: string;
	title?: string;
	productId: string;
}

function BasketResult({
	basket,
	errors,
}: {
	basket: BasketOutput | undefined;
	errors: readonly BasketError[];
}) {
	return (
		<>
			{errors.length ? (
				<ul className="flex flex-col gap-1 text-destructive text-sm">
					{errors.map((e) => (
						<li key={`${e.error}-${e.productId}`} dir="auto">
							{e.title ?? e.productId}:{" "}
							{e.error.replaceAll("_", " ")}
						</li>
					))}
				</ul>
			) : null}
			{basket ? <BasketLines basket={basket} /> : null}
		</>
	);
}

function ProductFacts({ product }: { product: Obj }) {
	const facts = [
		["Brand", product["brand"]],
		["Department", product["department"]],
		["Price", product["salePrice"] ?? product["price"]],
		["In stock", product["inStock"] ? "Yes" : "No"],
	] as const;
	return (
		<dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm">
			{facts.map(([k, v]) =>
				v == null ? null : (
					<div key={k} className="contents">
						<dt className="text-muted-foreground">{k}</dt>
						<dd dir="auto" className="truncate">
							{String(v)}
						</dd>
					</div>
				),
			)}
		</dl>
	);
}

/**
 * Opens the search tester with the exact words the agent searched, so a
 * retailer can see why products did or didn't come up. Renders nothing
 * outside a retailer's admin (no slug) or without a query.
 */
function CheckSearchLink({ query }: { query: string }) {
	const { organizationSlug } = useParams({ strict: false }) as {
		organizationSlug?: string;
	};
	if (!organizationSlug || !query.trim()) {
		return null;
	}
	return (
		<Tooltip>
			<TooltipTrigger
				render={
					<Link
						to="/app/$organizationSlug/search"
						params={{ organizationSlug }}
						search={{ q: query }}
						aria-label="Check this search"
						className={buttonVariants({
							variant: "ghost",
							size: "icon-sm",
							className: "me-1 shrink-0 text-muted-foreground",
						})}
					/>
				}
			>
				<ScanSearchIcon />
			</TooltipTrigger>
			<TooltipContent>Check this search</TooltipContent>
		</Tooltip>
	);
}

export function BasketLines({ basket }: { basket: BasketOutput }) {
	if (basket.lines.length === 0) {
		return (
			<p className="text-muted-foreground text-sm">
				The basket is empty.
			</p>
		);
	}
	return (
		<div className="flex flex-col gap-2 text-sm">
			<ul className="flex flex-col gap-1.5">
				{basket.lines.map((line) => (
					<li
						key={line.productId}
						className="flex items-baseline gap-2"
					>
						<span className="w-7 shrink-0 text-muted-foreground tabular">
							{line.quantity}×
						</span>
						<span
							dir="auto"
							className={cn(
								"min-w-0 flex-1 truncate",
								!line.inStock &&
									"text-muted-foreground line-through",
							)}
						>
							{line.title}
						</span>
						<span className="shrink-0 tabular">
							{formatMoney(line.lineTotal, basket.currency)}
						</span>
					</li>
				))}
			</ul>
			<div className="flex justify-between border-border/70 border-t pt-2 font-medium">
				<span>Total</span>
				<span className="tabular">
					{formatMoney(basket.total, basket.currency)}
				</span>
			</div>
		</div>
	);
}

function ProductRow({
	product: p,
	rank,
}: {
	product: ProductCard;
	rank?: number;
}) {
	const details = [p.brand, p.size, p.department].filter(Boolean).join(" · ");
	return (
		<div className="flex items-center gap-2.5 py-1.5">
			{rank ? (
				<span className="w-4 shrink-0 text-right text-muted-foreground text-xs tabular">
					{rank}
				</span>
			) : null}
			<div className="size-9 shrink-0 overflow-hidden rounded-lg bg-background ring-1 ring-foreground/5">
				{p.imageUrl ? (
					<img
						src={p.imageUrl}
						alt=""
						loading="lazy"
						className="size-full object-contain p-0.5"
					/>
				) : null}
			</div>
			<div className="min-w-0 flex-1">
				<p dir="auto" className="truncate text-sm">
					{p.title}
				</p>
				{details ? (
					<p className="truncate text-muted-foreground text-xs">
						{details}
					</p>
				) : null}
			</div>
			<div className="flex shrink-0 flex-col items-end text-xs tabular">
				<span className="font-medium text-foreground">
					{formatMoney(p.salePrice ?? p.price, p.currency)}
				</span>
				{!p.inStock ? (
					<span className="text-destructive">Out of stock</span>
				) : p.salePrice ? (
					<span className="text-muted-foreground line-through">
						{formatMoney(p.price, p.currency)}
					</span>
				) : null}
			</div>
		</div>
	);
}

function RawData({ input, output }: { input: unknown; output: unknown }) {
	return (
		<Collapsible>
			<CollapsibleTrigger className="group/raw inline-flex items-center gap-1.5 rounded-lg text-muted-foreground text-xs outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/30">
				<CodeIcon className="size-3.5" aria-hidden />
				<span className="group-data-[panel-open]/raw:hidden">
					Show raw data
				</span>
				<span className="hidden group-data-[panel-open]/raw:inline">
					Hide raw data
				</span>
			</CollapsibleTrigger>
			<CollapsibleContent className="mt-2 grid gap-2">
				<Json label="Input" value={input} />
				{output !== undefined ? (
					<Json label="Output" value={output} />
				) : null}
			</CollapsibleContent>
		</Collapsible>
	);
}

function Json({ label, value }: { label: string; value: unknown }) {
	return (
		<div className="min-w-0">
			<p className="mb-1 text-muted-foreground text-xs">{label}</p>
			<pre className="max-h-64 overflow-auto rounded-xl bg-muted/60 p-2.5 font-mono text-[11px] leading-relaxed">
				{JSON.stringify(value, null, 2)}
			</pre>
		</div>
	);
}
